import { LDateTime } from '@app/components/date/LDateTime';
import { Station } from '@app/core/types/stations';
import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { Box, DialogTitle, IconButton, Typography } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { getVerifyMatchDatetime, getVerifyMatchMaterialId } from './verify-match-target-utils';
import { VerifyMatchDialogNavigationState, VerifyMatchTarget } from './verify-match-types';
import { VerifyMatchDialogNavigation } from './VerifyMatchDialogNavigation';

const dialogTitleMessage = defineMessage({
  id: 'audit.verifyMatch.dialogTitle',
  defaultMessage: 'Verificar detección',
});

export interface VerifyMatchDialogTitleProps {
  station: Station;
  target: VerifyMatchTarget;
  onClose: () => void;
  navigation?: VerifyMatchDialogNavigationState;
}

/**
 * Dialog header with the verify action name, material ID, station, detection time,
 * optional prev/next list navigation, and close button.
 */
export function VerifyMatchDialogTitle({
  station,
  target,
  onClose,
  navigation,
}: VerifyMatchDialogTitleProps) {
  const materialId = getVerifyMatchMaterialId(target);
  const datetime = getVerifyMatchDatetime(target);

  return (
    <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <FactCheckOutlinedIcon sx={{ mt: 0.25 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <FormattedMessage {...dialogTitleMessage} />
          {materialId && (
            <Typography component="span" variant="inherit" noWrap>
              {materialId}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            mt: 0.25,
          }}
        >
          <StationLabel station={station} size="sm" indicatorsDisplay={false} />
          <Typography variant="caption" color="text.secondary">
            ·
          </Typography>
          <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
            <LDateTime value={datetime} showSeconds />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {navigation && <VerifyMatchDialogNavigation navigation={navigation} />}
        <IconButton onClick={onClose} sx={{ my: 0 }}>
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
  );
}
